import { Trash2, Plus } from "lucide-react";
import type { InventoryRow } from "../types";
import UnitCombobox from "./UnitCombobox";

interface Props {
  rows: InventoryRow[];
  onChange: (rows: InventoryRow[]) => void;
}

export default function PreviewTable({ rows, onChange }: Props) {
  const update = (idx: number, field: keyof InventoryRow, val: string | number) => {
    const updated = rows.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, [field]: val };
      if (field === "quantity" || field === "unit_price") {
        next.total = next.quantity * next.unit_price;
      }
      return next;
    });
    onChange(updated);
  };

  const remove = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx));
  };

  const add = () => {
    onChange([...rows, { item: "", quantity: 1, unit: "", unit_price: 0, total: 0 }]);
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto -mx-4 sm:-mx-0 border border-light-border dark:border-navy-700/40 rounded-2xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-navy-900 border-b border-light-border dark:border-navy-700/60 text-gray-500 dark:text-metallic-silver text-xs uppercase tracking-widest">
              <th className="text-left py-3 pl-4">Item</th>
              <th className="text-right py-3 w-24">Qty</th>
              <th className="text-left py-3 w-28">Unit</th>
              <th className="text-right py-3 w-32">Price (₦)</th>
              <th className="text-right py-3 w-32 pr-4">Total (₦)</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-gray-400 dark:text-metallic-silver/50 text-sm">
                  No items yet. Speak or type to add.
                </td>
              </tr>
            )}
            {rows.map((row, idx) => (
              <tr key={idx} className="border-b border-light-border dark:border-navy-800/40 hover:bg-gray-50 dark:hover:bg-navy-800/30">
                <td className="py-2 pl-4">
                  <input
                    value={row.item}
                    onChange={(e) => update(idx, "item", e.target.value)}
                    className="w-full bg-transparent text-gray-900 dark:text-white px-2 py-1 rounded focus:outline-none focus:bg-white dark:focus:bg-navy-700 focus:border-citilux-blue-500 border border-transparent focus:border"
                    placeholder="Item name"
                  />
                </td>
                <td className="py-2">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={row.quantity}
                    onChange={(e) => update(idx, "quantity", parseFloat(e.target.value) || 0)}
                    className="w-full bg-transparent text-gray-900 dark:text-white text-right px-2 py-1 rounded focus:outline-none focus:bg-white dark:focus:bg-navy-700 focus:border-citilux-blue-500 border border-transparent focus:border tabular-nums"
                  />
                </td>
                <td className="py-2">
                  <UnitCombobox
                    value={row.unit}
                    onChange={(v) => update(idx, "unit", v)}
                    className="border-transparent focus:border bg-transparent focus:bg-white dark:focus:bg-navy-700 rounded"
                  />
                </td>
                <td className="py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={row.unit_price}
                    onChange={(e) => update(idx, "unit_price", parseFloat(e.target.value) || 0)}
                    className="w-full bg-transparent text-gray-900 dark:text-white text-right px-2 py-1 rounded focus:outline-none focus:bg-white dark:focus:bg-navy-700 focus:border-citilux-blue-500 border border-transparent focus:border tabular-nums"
                  />
                </td>
                <td className="py-2 pr-4 text-right font-semibold text-citilux-blue-600 dark:text-citilux-blue-400 tabular-nums">
                  {(row.quantity * row.unit_price).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </td>
                <td className="py-2 text-center">
                  <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={add}
        className="flex items-center gap-2 text-sm text-citilux-blue-600 dark:text-citilux-blue-400 font-medium hover:underline px-1"
      >
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
}
